import { useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import useFetch from "hooks/useFetch";

export interface AdGroup {
  id: string;
  name: string;
}

export interface StorageGateway {
  auth_type: string;
  description: string;
  id: string;
  ip_address: string;
  name: string;
  region: string;
  state: string;
  status: string;
  allowed_ad_groups: AdGroup[];
}

interface StorageGatewaysResponse {
  total: number;
  gateways: StorageGateway[];
}

export const GATEWAY_PAGE_SIZE = 10;

export const useStorageGateways = (page = 1) => {
  const msalInstance = useMsal();
  const url = `v2/storage-gateways?page=${page}&page_size=${GATEWAY_PAGE_SIZE}`;
  const { data, error, isLoading, fetchData } =
    useFetch<StorageGatewaysResponse>(url, msalInstance);

  return {
    gateways: data?.gateways ?? [],
    total: data?.total ?? 0,
    loading: isLoading,
    error: error?.message || null,
    fetchGateways: fetchData,
  };
};

export const useStorageGatewaysByIds = (ids: string[]) => {
  const msalInstance = useMsal();
  const url = useMemo(
    () =>
      ids.length > 0
        ? `v2/storage-gateways?${[...ids]
            .sort()
            .map(id => `id=${encodeURIComponent(id)}`)
            .join("&")}`
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ids.join(",")],
  );
  const { data, error, isLoading } = useFetch<StorageGatewaysResponse>(
    url,
    msalInstance,
  );

  return {
    gateways: data?.gateways ?? [],
    loading: isLoading,
    error: error?.message || null,
  };
};

export interface FileShare {
  id: string;
  name: string;
  description: string;
  gateway_id: string;
  prefix: string;
  status: string;
  share_type: "SMB" | "NFS";
}

export const useFileShares = (pondId: string) => {
  const msalInstance = useMsal();
  const url = pondId
    ? `v2/s3buckets/${pondId}/storage-gateways/shares/`
    : undefined;
  const { data, error, isLoading, fetchData } = useFetch<FileShare[]>(
    url,
    msalInstance,
  );

  return {
    shares: Array.isArray(data) ? data : [],
    loading: isLoading,
    error: error?.message || null,
    fetchShares: fetchData,
  };
};
